import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Plus, Pencil, Trash2, Search, Store } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STORE_CATEGORIES = [
  "Fashion", "Electronics", "Food & Beverage", "Beauty & Wellness", 
  "Home & Living", "Sports & Fitness", "Entertainment", "Services", "Other"
];

export default function Stores() {
  const { user, getAuthHeader } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    floor: "",
    unit_number: "",
    phone: "",
    email: "",
    description: "",
    opening_hours: "10:00 AM - 10:00 PM",
    logo_url: "",
    is_active: true
  });

  const canEdit = ["admin", "manager"].includes(user?.role);
  const canDelete = user?.role === "admin";

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stores`, {
        headers: getAuthHeader()
      });
      setStores(response.data);
    } catch (error) {
      toast.error("Failed to fetch stores");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStore) {
        await axios.put(`${API_URL}/api/stores/${editingStore.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Store updated successfully");
      } else {
        await axios.post(`${API_URL}/api/stores`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Store created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm("Are you sure you want to delete this store?")) return;
    try {
      await axios.delete(`${API_URL}/api/stores/${storeId}`, {
        headers: getAuthHeader()
      });
      toast.success("Store deleted successfully");
      fetchStores();
    } catch (error) {
      toast.error("Failed to delete store");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      category: "",
      floor: "",
      unit_number: "",
      phone: "",
      email: "",
      description: "",
      opening_hours: "10:00 AM - 10:00 PM",
    logo_url: "",
      is_active: true
    });
    setEditingStore(null);
  };

  const openEditDialog = (store) => {
    setEditingStore(store);
    setFormData({
      name: store.name,
      category: store.category,
      floor: store.floor,
      unit_number: store.unit_number,
      phone: store.phone || "",
      email: store.email || "",
      description: store.description || "",
      opening_hours: store.opening_hours || "10:00 AM - 10:00 PM",
      is_active: store.is_active
    });
    setDialogOpen(true);
  };

  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    store.category.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((left, right) => String(left?.name || '').localeCompare(String(right?.name || ''), undefined, { sensitivity: 'base' }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="stores-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Stores</h1>
          <p className="text-muted-foreground mt-1">Manage mall store directory</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-gradient rounded-none" data-testid="add-store-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Store
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl rounded-none">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">
                  {editingStore ? "Edit Store" : "Add New Store"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Store Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="rounded-none"
                      required
                      data-testid="store-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger className="rounded-none" data-testid="store-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {STORE_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Floor *</Label>
                    <Input
                      value={formData.floor}
                      onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                      placeholder="e.g., G, 1, 2"
                      className="rounded-none"
                      required
                      data-testid="store-floor-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit Number *</Label>
                    <Input
                      value={formData.unit_number}
                      onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                      placeholder="e.g., G-15"
                      className="rounded-none"
                      required
                      data-testid="store-unit-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="rounded-none"
                      data-testid="store-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="rounded-none"
                      data-testid="store-email-input"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Opening Hours</Label>
                    <Input
                      value={formData.opening_hours}
                      onChange={(e) => setFormData({ ...formData, opening_hours: e.target.value })}
                      className="rounded-none"
                      data-testid="store-hours-input"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="rounded-none"
                      rows={3}
                      data-testid="store-description-input"
                    />
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                  <div className="space-y-2">
                    <Label>Logo URL</Label>
                    <Input
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      placeholder="https://example.com/logo.png"
                      className="rounded-none"
                    />
                    <p className="text-xs text-muted-foreground">Optional: URL to store logo image</p>
                  </div>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      data-testid="store-active-switch"
                    />
                    <Label>Active</Label>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-gradient rounded-none" data-testid="store-submit-btn">
                    {editingStore ? "Update" : "Create"} Store
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <Card className="border-border rounded-none">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search stores by name or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-none"
              data-testid="store-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border rounded-none">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-pulse text-muted-foreground">Loading stores...</div>
            </div>
          ) : filteredStores.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <Store className="h-12 w-12 mb-3 opacity-50" />
              <p>No stores found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Category</TableHead>
                  <TableHead className="font-semibold">Location</TableHead>
                  <TableHead className="font-semibold">Phone</TableHead>
                  <TableHead className="font-semibold">Hours</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  {canEdit && <TableHead className="font-semibold text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStores.map((store) => (
                  <TableRow key={store.id} className="table-row" data-testid={`store-row-${store.id}`}>
                    <TableCell className="font-medium">{store.name}</TableCell>
                    <TableCell>{store.category}</TableCell>
                    <TableCell className="font-mono text-sm">Floor {store.floor}, Unit {store.unit_number}</TableCell>
                    <TableCell className="font-mono text-sm">{store.phone || "-"}</TableCell>
                    <TableCell className="text-sm">{store.opening_hours}</TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium ${
                        store.is_active 
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {store.is_active ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(store)}
                            data-testid={`edit-store-${store.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(store.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-store-${store.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
